<template>
    <div class="">
        <div class="p-4 rounded col-span-2 flex space-x-2" v-if="question.responses.correct_answer">
            <CorrectIncorrectIcon :is-correct="true" />

            <span class="pl-1">
                <div class="w-full p-2 flex gap-2 items-center" v-if="question.type === 'math-multiple-magnitudes'">
                    <div v-for="index in [0, 1, 2]" :key="index">
                        <BadgePill
                            class=""
                            color="gray"
                            v-if="question.responses.correct_answer.parts['label_' + index]"
                        >
                            <span>{{ question.responses.correct_answer.parts['value_' + index] }}</span>
                            <LatexMaybe
                                :model-value="question.responses.correct_answer.parts['label_' + index]"
                                :is-latex="question.is_latex_answers"
                            />
                        </BadgePill>
                    </div>
                </div>
                <div class="w-full p-2 flex gap-2 items-center" v-else-if="question.type === 'math-prime-factors'">
                    <div class="px-2 py-1 bg-gray-100 tabular-nums">
                        <PrimeFactorsDisplay :factors="question.responses.correct_answer.factors" />
                    </div>
                </div>
                <div
                    class="w-full p-2 flex gap-2 items-center"
                    v-else-if="question.type === 'math-polynomial-operation'"
                >
                    <div class="px-2 py-1 bg-gray-100 tabular-nums">
                        <PolynomialDisplay :polynomial="question.responses.correct_answer.polynomial" />
                    </div>
                </div>
                <LatexDisplay
                    v-else-if="question.is_latex_answers"
                    :model-value="question.responses.correct_answer.text"
                />
                <span v-else>
                    {{ question.responses.correct_answer.text }}
                </span>
            </span>
            <template v-if="question.responses.correct_answer">
                <div class="inline-block" style="min-height: 32px">
                    <StudentsProfileStackFiltered
                        :students="students"
                        :selected="question.responses.correct_answer.student_ids"
                        class="inline-block"
                    />
                </div>
                <div v-if="question.responses.correct_answer.percent !== null && withPercent">
                    <span>{{ question.responses.correct_answer.percent }}%</span>
                    <span v-if="question.responses.correct_answer.count !== null" class="text-sm text-gray-500 pl-1"
                        >({{ question.responses.correct_answer.count }})</span
                    >
                </div>
            </template>
        </div>

        <div class="p-4" v-for="response in question.responses.correct_answers">
            <div class="rounded col-span-2 flex space-x-2">
                <CorrectIncorrectIcon :is-correct="true" />
                <span class="pl-1">
                    <div class="w-full p-2 flex gap-2 items-center" v-if="question.type === 'math-multiple-magnitudes'">
                        <div v-for="index in [0, 1, 2]" :key="index">
                            <BadgePill
                                class=""
                                color="gray"
                                v-if="question.responses.correct_answer.parts['label_' + index]"
                            >
                                <span>{{ question.responses.correct_answer.parts['value_' + index] }}</span>
                                <LatexMaybe
                                    :model-value="question.responses.correct_answer.parts['label_' + index]"
                                    :is-latex="question.is_latex_answers"
                                />
                            </BadgePill>
                        </div>
                    </div>
                    <LatexDisplay v-else-if="question.is_latex_answers" :model-value="response.text" />
                    <span v-else> {{ response.text }} </span>
                </span>
                <div class="inline-block" style="min-height: 32px">
                    <StudentsProfileStackFiltered
                        :students="students"
                        :selected="response.student_ids"
                        class="inline-block"
                    />
                </div>
                <div v-if="response.percent !== null && withPercent">
                    <span> {{ response.percent }}% </span>
                    <span v-if="response.count !== null" class="text-sm text-gray-500 pl-1"
                        >({{ response.count }})</span
                    >
                </div>
            </div>
            <div class="pt-1 flex space-x-2" v-if="response.annotations">
                <BadgePill :color="annotation.color" :key="annotation.text" v-for="annotation in response.annotations">
                    {{ annotation.text }}
                </BadgePill>
            </div>
        </div>

        <div class="p-4" v-for="response in question.responses.other" :key="response.text">
            <div class="rounded col-span-2 flex space-x-2">
                <CorrectIncorrectIcon :is-correct="false" />
                <span class="pl-1">
                    <div class="w-full p-2 flex gap-2 items-center" v-if="question.type === 'math-prime-factors'">
                        <div class="px-2 py-1 bg-gray-100 tabular-nums">
                            <PrimeFactorsDisplay :factors="response.factors" />
                        </div>
                    </div>
                    <div
                        class="w-full p-2 flex gap-2 items-center"
                        v-else-if="question.type === 'math-polynomial-operation'"
                    >
                        <div class="px-2 py-1 bg-gray-100 tabular-nums">
                            <PolynomialDisplay :polynomial="response.polynomial" />
                        </div>
                    </div>
                    <LatexDisplay v-else-if="question.is_latex_answers" :model-value="response.text" />
                    <span v-else> {{ response.text }} </span>
                </span>
                <div class="inline-block" style="min-height: 32px">
                    <StudentsProfileStackFiltered
                        :students="students"
                        :selected="response.student_ids"
                        class="inline-block"
                    />
                </div>
                <div v-if="response.percent !== null && withPercent">
                    <span> {{ response.percent }}% </span>
                    <span v-if="response.count !== null" class="text-sm text-gray-500 pl-1"
                        >({{ response.count }})</span
                    >
                </div>
            </div>
            <div class="pt-1 flex space-x-2" v-if="response.annotations">
                <BadgePill :color="annotation.color" :key="annotation.text" v-for="annotation in response.annotations">
                    {{ annotation.text }}
                </BadgePill>
            </div>
        </div>

        <div class="p-4 rounded col-span-2 flex space-x-2" v-if="question.responses.unanswered">
            <svg
                class="w-6 h-6 text-gray-300"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                    clip-rule="evenodd"
                ></path>
            </svg>
            <span class="pl-1">
                {{ question.responses.unanswered.text }}
            </span>
            <template v-if="question.responses.unanswered">
                <div class="inline-block" style="min-height: 32px">
                    <StudentsProfileStackFiltered
                        :students="students"
                        :selected="question.responses.unanswered.student_ids"
                        class="inline-block"
                    />
                </div>
                <div v-if="question.responses.unanswered.percent !== null && withPercent">
                    <span> {{ question.responses.unanswered.percent }}% </span>
                    <span v-if="question.responses.unanswered.count !== null" class="text-sm text-gray-500 pl-1"
                        >({{ question.responses.unanswered.count }})</span
                    >
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import CorrectIncorrectIcon from '@/components/Quiz/CorrectIncorrectIcon.vue';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import BadgePill from '@/components/BadgePill.vue';
import LatexDisplay from '@/components/LatexDisplay.vue';
import LatexMaybe from '@/components/LatexMaybe.vue';
import PrimeFactorsDisplay from '@/components/Quiz/PrimeFactorsDisplay.vue';
import PolynomialDisplay from '@/components/Quiz/PolynomialDisplay.vue';

export default {
    name: 'QuestionGeneralReview',
    components: {
        PolynomialDisplay,
        PrimeFactorsDisplay,
        LatexMaybe,
        LatexDisplay,
        CorrectIncorrectIcon,
        StudentsProfileStackFiltered,
        BadgePill,
    },
    props: {
        question: {
            required: true,
        },
        withPercent: {
            required: false,
            default: false,
            type: Boolean,
        },
        students: {
            required: true,
            type: Array,
        },
    },
};
</script>
